import React, { useState, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  FaCircle, 
  FaCalendarAlt, 
  FaPaperclip,
  FaMapMarkerAlt
} from 'react-icons/fa';
import { Avatar } from '../Avatar';
import { getRiskColor, DesignSystem } from '../../styles/designSystem';

// Status columns configuration
const STATUS_COLUMNS = [
  { id: 'NOT_STARTED', title: 'Not Started', color: '#888', bgColor: 'bg-[#fafafa]', borderColor: 'border-[#eaeaea]' },
  { id: 'IN_PROGRESS', title: 'In Progress', color: '#3B82F6', bgColor: 'bg-[#f5f5f5]', borderColor: 'border-[#eaeaea]' },
  { id: 'ON_HOLD', title: 'On Hold', color: '#F59E0B', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' },
  { id: 'COMPLETED', title: 'Completed', color: '#10B981', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200' },
];

interface KanbanCardProps {
  task: ProjectItemInterface;
  projectData: ProjectInterface;
  rooms: any[];
  isDragging?: boolean;
}

// Draggable Kanban Card
const KanbanCard: React.FC<KanbanCardProps> = ({ task, projectData, rooms, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: task.id! });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const owner = projectData?.users?.find(u => u.id === task.owner_id);
  const assignee = projectData?.users?.find(u => u.id === task.assigned_to);
  const phase = projectData?.phases?.find(p => p.id === task.phase_id);
  const room = rooms?.find(r => r.room_id === task.location?.room_id);
  const dueDate = task.due_date ? new Date(task.due_date) : null;
  const isOverdue = dueDate && dueDate < new Date() && task.status !== 'COMPLETED';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`
        group bg-white rounded-xl border border-[#f5f5f5] p-4 mb-3 
        shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-all duration-200 cursor-grab active:cursor-grabbing
        ${isDragging ? 'opacity-50 shadow-[0_4px_12px_rgba(0,0,0,0.08)] ring-2 ring-[#171717]' : ''}
      `}
    >
      {/* Risk Indicator Row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {/* Risk Level */}
          <FaCircle 
            className="w-2 h-2" 
            style={{ color: getRiskColor(task.risks || '') }}
          />
          <span className="text-[10px] uppercase tracking-wide text-[#aaa]">
            {task.risks || 'None'}
          </span>
        </div>
      </div>

      {/* Task Name */}
      <h4 className="text-sm font-medium text-[#171717] mb-2 line-clamp-2 leading-snug">
        {task.name || task.title || 'Untitled Task'}
      </h4>

      {/* Description Preview */}
      {task.description && (
        <p className="text-xs text-[#888] mb-3 line-clamp-2 leading-relaxed">
          {task.description}
        </p>
      )}

      {/* Phase Badge */}
      {phase && (
        <div className="mb-3">
          <span 
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium"
            style={{ 
              backgroundColor: `${DesignSystem.colors.brand[100]}`,
              color: DesignSystem.colors.brand[700]
            }}
          >
            {phase.name}
          </span>
        </div>
      )}

      {/* Location */}
      {room && (
        <div className="flex items-center gap-1.5 mb-3 text-xs text-[#888]">
          <FaMapMarkerAlt className="w-3 h-3" />
          <span>{room.room_name}</span>
        </div>
      )}

      {/* Progress Bar */}
      {task.completion_percentage !== undefined && task.completion_percentage !== null && task.completion_percentage > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-[10px] text-[#aaa] mb-1">
            <span>Progress</span>
            <span>{task.completion_percentage}%</span>
          </div>
          <div className="w-full bg-[#f5f5f5] rounded-full h-1.5">
            <div 
              className="bg-[#171717] h-1.5 rounded-full transition-all duration-300" 
              style={{ width: `${task.completion_percentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Footer: Due Date, Attachments, Avatars */}
      <div className="flex items-center justify-between pt-2 border-t border-[#f5f5f5]">
        <div className="flex items-center gap-3">
          {/* Due Date */}
          {dueDate && (
            <div className={`flex items-center gap-1 text-[11px] ${isOverdue ? 'text-red-500' : 'text-[#aaa]'}`}>
              <FaCalendarAlt className="w-3 h-3" />
              <span>
                {dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </div>
          )}

          {/* Document Indicator */}
          {task.document && (
            <div className="flex items-center gap-1 text-[11px] text-[#aaa]">
              <FaPaperclip className="w-3 h-3" />
            </div>
          )}
        </div>

        {/* Avatars */}
        <div className="flex items-center -space-x-1">
          {owner && (
            <Avatar
              firstName={owner.first_name}
              lastName={owner.last_name}
              email={owner.email}
              size={22}
              className="ring-2 ring-white"
            />
          )}
          {assignee && assignee.id !== owner?.id && (
            <Avatar
              firstName={assignee.first_name}
              lastName={assignee.last_name}
              email={assignee.email}
              size={22}
              className="ring-2 ring-white"
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Droppable Column
interface DroppableColumnProps {
  column: typeof STATUS_COLUMNS[0];
  tasks: ProjectItemInterface[];
  projectData: ProjectInterface;
  rooms: any[];
}

const DroppableColumn: React.FC<DroppableColumnProps> = ({ column, tasks, projectData, rooms }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
  });

  const taskIds = useMemo(() => tasks.map(t => t.id!), [tasks]);

  return (
    <div className={`flex flex-col min-w-[312px] w-[312px] ${column.bgColor} rounded-2xl border ${column.borderColor} ${isOver ? 'ring-2 ring-[#171717]' : ''}`}>
      {/* Column Header */}
      <div className="p-4 border-b border-[#f5f5f5]/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: column.color }}
            />
            <h3 className="text-sm font-semibold text-[#444]">
              {column.title}
            </h3>
          </div>
          <span 
            className="text-xs font-medium px-2 py-0.5 rounded-full"
            style={{ 
              backgroundColor: `${column.color}20`,
              color: column.color 
            }}
          >
            {tasks.length}
          </span>
        </div>
      </div>

      {/* Cards Container */}
      <div 
        ref={setNodeRef}
        className="flex-1 p-3 overflow-y-auto max-h-[calc(100vh-300px)] min-h-[200px] custom-scrollbar"
      >
        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
          {tasks.map(task => (
            <KanbanCard 
              key={task.id} 
              task={task} 
              projectData={projectData}
              rooms={rooms}
            />
          ))}
        </SortableContext>

        {/* Empty State */}
        {tasks.length === 0 && (
          <div className={`flex flex-col items-center justify-center py-8 text-[#aaa] border-2 border-dashed rounded-xl ${isOver ? 'border-indigo-300 bg-[#f5f5f5]/50' : 'border-[#eaeaea]'}`}>
            <div className="w-10 h-10 rounded-full bg-[#f5f5f5] flex items-center justify-center mb-2">
              <svg className="w-5 h-5 text-[#ccc]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-xs">No tasks</p>
            <p className="text-[10px] text-[#ccc] mt-1">Drag tasks here</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Card being dragged overlay
const DragOverlayCard: React.FC<{ task: ProjectItemInterface; projectData: ProjectInterface; rooms: any[] }> = ({ task, projectData, rooms }) => {
  return (
    <div className="bg-white rounded-xl border border-[#eaeaea] p-4 shadow-[0_8px_30px_rgba(0,0,0,0.12)] ring-2 ring-[#171717] w-[312px] rotate-3">
      <KanbanCard task={task} projectData={projectData} rooms={rooms} isDragging />
    </div>
  );
};

// Main Kanban Board Component
interface KanbanBoardProps {
  projectId: string;
  projectData: ProjectInterface;
  filteredTasks: ProjectItemInterface[];
  rooms: any[];
  onUpdateTaskSilent: (task: ProjectItemInterface) => Promise<void>;
  onProjectUpdate?: () => Promise<void>;
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  projectData,
  filteredTasks,
  rooms,
  onUpdateTaskSilent,
  onProjectUpdate,
}) => {
  const [activeTask, setActiveTask] = useState<ProjectItemInterface | null>(null);
  // Local state for optimistic updates
  const [localTasks, setLocalTasks] = useState<ProjectItemInterface[]>(filteredTasks);

  // Sync local tasks with filtered tasks when they change
  React.useEffect(() => {
    setLocalTasks(filteredTasks);
  }, [filteredTasks]);

  // Group tasks by status using local state for instant updates
  const tasksByStatus = useMemo(() => {
    const grouped: Record<string, ProjectItemInterface[]> = {};
    STATUS_COLUMNS.forEach(col => {
      grouped[col.id] = localTasks.filter(task => task.status === col.id);
    });
    return grouped;
  }, [localTasks]);

  // Sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const task = filteredTasks.find(t => t.id === active.id);
    if (task) {
      setActiveTask(task);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const activeTaskId = active.id as string;
    const overId = over.id as string;

    // Find the target column - either by dropping on a column directly or on a task in that column
    let targetColumnId: string | null = null;

    // Check if dropped on a column directly
    if (STATUS_COLUMNS.some(col => col.id === overId)) {
      targetColumnId = overId;
    } else {
      // Find which column contains the task we're dropping on
      for (const col of STATUS_COLUMNS) {
        if (tasksByStatus[col.id]?.some(t => t.id === overId)) {
          targetColumnId = col.id;
          break;
        }
      }
    }

    if (!targetColumnId) return;

    const task = filteredTasks.find(t => t.id === activeTaskId);
    if (!task || task.status === targetColumnId) return;

    // Optimistic update - update local state immediately
    const updatedTask = { ...task, status: targetColumnId as ProjectStatusTypes };
    setLocalTasks(prev => prev.map(t => t.id === task.id ? updatedTask : t));
    
    // Then update on server (silently, no toast)
    try {
      await onUpdateTaskSilent(updatedTask);
      // Sync with server data
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error) {
      console.error('Failed to update task status:', error);
      // Revert on error
      setLocalTasks(prev => prev.map(t => t.id === task.id ? task : t));
    }
  };

  return (
    <div className="kanban-board overflow-x-auto">
      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.1);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: rgba(0, 0, 0, 0.2);
        }
        .kanban-board::-webkit-scrollbar {
          height: 8px;
        }
        .kanban-board::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .kanban-board::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .kanban-board::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 pb-4">
          {STATUS_COLUMNS.map(column => (
            <DroppableColumn
              key={column.id}
              column={column}
              tasks={tasksByStatus[column.id] || []}
              projectData={projectData}
              rooms={rooms}
            />
          ))}
        </div>

        <DragOverlay>
          {activeTask && (
            <DragOverlayCard 
              task={activeTask} 
              projectData={projectData}
              rooms={rooms}
            />
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default KanbanBoard;
